import {
  DeesElement,
  customElement,
  html,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import { dashboardCardStyles } from './sz-dashboard-card.styles.js';

declare global {
  interface HTMLElementTagNameMap {
    'sz-resource-usage-card': SzResourceUsageCard;
  }
}

export interface IResourceUsage {
  cpu: number;
  memoryUsed: string;
  memoryTotal: string;
  memoryPercent?: number;
  networkIn: string;
  networkOut: string;
  topConsumers: Array<{ name: string; memory: string }>;
}

@customElement('sz-resource-usage-card')
export class SzResourceUsageCard extends DeesElement {
  public static demo = () => html`
    <div style="padding: 24px; max-width: 800px;">
      <sz-resource-usage-card
        .data=${{
          cpu: 0.5,
          memoryUsed: '191 MB',
          memoryTotal: '429.2 GB',
          memoryPercent: 0.04,
          networkIn: '38.9 KB/s',
          networkOut: '1.7 KB/s',
          topConsumers: [
            { name: 'test-nginx', memory: '32.1 MB' },
            { name: 'test-v2', memory: '31.7 MB' },
          ],
        }}
        serviceCount="7"
      ></sz-resource-usage-card>
    </div>
  `;

  public static demoGroups = ['Dashboard'];

  @property({ type: Object })
  public accessor data: IResourceUsage = {
    cpu: 0,
    memoryUsed: '0 MB',
    memoryTotal: '0 GB',
    networkIn: '0 KB/s',
    networkOut: '0 KB/s',
    topConsumers: [],
  };

  @property({ type: String })
  public accessor serviceCount: string = '0';

  public static styles = [
    cssManager.defaultStyles,
    dashboardCardStyles,
    css`
      .metrics {
        display: flex;
        flex-direction: column;
        gap: var(--dees-spacing-lg);
      }

      dees-progressbar {
        margin: -8px;
      }

      .network-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--dees-spacing-md);
      }

      .network-metric {
        min-width: 0;
        padding: var(--dees-spacing-md);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-fill-secondary);
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
      }

      .network-metric.download dees-icon {
        color: var(--dees-color-accent-success);
      }

      .network-metric.upload dees-icon {
        color: var(--dees-color-accent-primary);
      }

      .network-copy {
        min-width: 0;
        display: flex;
        flex-direction: column;
      }

      .network-label {
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size);
        line-height: var(--dees-font-caption1-line);
      }

      .network-value {
        overflow: hidden;
        color: var(--dees-color-text-primary);
        font-family: var(--dees-font-family-mono);
        font-size: var(--dees-font-callout-size);
        line-height: 1.35;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .consumers {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        gap: var(--dees-spacing-xs) var(--dees-spacing-md);
      }

      .consumer {
        white-space: nowrap;
      }

      .consumer-name {
        color: var(--dees-color-text-secondary);
      }

      @media (max-width: 460px) {
        .network-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <dees-tile>
        <div slot="header" class="card-header">
          <div class="header-copy">
            <div class="card-title">Resource Usage</div>
            <div class="card-subtitle">Aggregated across ${this.serviceCount} services</div>
          </div>
          <dees-icon icon="lucide:Activity" .iconSize=${18}></dees-icon>
        </div>

        <div class="card-content">
          <div class="metrics">
            <dees-progressbar
              label="CPU"
              .percentage=${this.clampPercentage(this.data.cpu)}
            ></dees-progressbar>
            <dees-progressbar
              .label=${`Memory · ${this.data.memoryUsed} / ${this.data.memoryTotal}`}
              .percentage=${this.calculateMemoryPercent()}
            ></dees-progressbar>

            <div class="network-grid" aria-label="Live container network throughput">
              <div class="network-metric download">
                <dees-icon icon="lucide:ArrowDown" .iconSize=${16}></dees-icon>
                <div class="network-copy">
                  <span class="network-label">Download</span>
                  <span class="network-value">${this.data.networkIn}</span>
                </div>
              </div>
              <div class="network-metric upload">
                <dees-icon icon="lucide:ArrowUp" .iconSize=${16}></dees-icon>
                <div class="network-copy">
                  <span class="network-label">Upload</span>
                  <span class="network-value">${this.data.networkOut}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        ${this.data.topConsumers.length > 0 ? html`
          <div slot="footer" class="card-footer">
            <div class="consumers">
              ${this.data.topConsumers.map(
                (consumer) => html`
                  <span class="consumer">
                    <span class="consumer-name">${consumer.name}</span>
                    · ${consumer.memory}
                  </span>
                `
              )}
            </div>
          </div>
        ` : ''}
      </dees-tile>
    `;
  }

  private calculateMemoryPercent(): number {
    if (Number.isFinite(this.data.memoryPercent)) {
      return this.clampPercentage(this.data.memoryPercent!);
    }
    const used = this.parseBytes(this.data.memoryUsed);
    const total = this.parseBytes(this.data.memoryTotal);
    if (used === null || total === null || total === 0) return 0;
    return this.clampPercentage((used / total) * 100);
  }

  private parseBytes(valueArg: string): number | null {
    const match = valueArg.trim().match(/^([\d.]+)\s*(B|KB|MB|GB|TB)$/i);
    if (!match) return null;
    const value = Number(match[1]);
    if (!Number.isFinite(value)) return null;
    const unitIndex = ['B', 'KB', 'MB', 'GB', 'TB'].indexOf(match[2].toUpperCase());
    return unitIndex >= 0 ? value * Math.pow(1024, unitIndex) : null;
  }

  private clampPercentage(valueArg: number): number {
    return Math.min(Math.max(Number.isFinite(valueArg) ? valueArg : 0, 0), 100);
  }
}
